/**
 * PATH: apps/ai/src/services/agents/query-analyzer.ts
 * PURPOSE: Analyzes user queries to determine intent, extract entities, detect portfolio scope
 */

import type { QueryIntent, ExtractedEntities, ConversationContext } from './agent-types';

export class QueryAnalyzerAgent {
  private static INTENT_PATTERNS: Record<string, RegExp[]> = {
    data_lookup: [/\b(show|get|find|list|what is|what are|tell me about)\b/i, /\bjob\s*#?\d+\b/i, /\bquote\s*#?\d+\b/i],
    search: [/\b(search|find|look for|where|which)\b.*\b(project|job|client|quote)\b/i, /\bnamed?\s+["']?[\w\s]+["']?\b/i],
    analysis: [/\b(analyze|compare|rank|top|worst|best|trend|performance)\b/i, /\b(margin|profit|cost|revenue|hours|budget)\b/i, /\b(at risk|overdue|outstanding)\b/i],
    action: [/\b(generate|create|draft|send|export|build)\b/i, /\b(report|email|pdf|document)\b/i],
    clarification: [/\bwhat do you mean\b/i, /\bcan you explain\b/i, /\bI don't understand\b/i],
  };

  private static DOMAIN_KEYWORDS: Record<string, string[]> = {
    jobs: ['job', 'project', 'site', 'work', 'contract'],
    quotes: ['quote', 'estimate', 'proposal', 'pricing'],
    invoices: ['invoice', 'payment', 'bill', 'receivable', 'outstanding'],
    timesheets: ['timesheet', 'hours', 'labour', 'labor', 'time', 'crew'],
    portfolio: ['portfolio', 'all', 'total', 'overall', 'summary', 'dashboard'],
  };

  private static PORTFOLIO_QUERY_PATTERNS: RegExp[] = [
    /\ball\s+jobs?\b/i, /\ball\s+\w+\s+jobs?\b/i, /\ball\s+\w+(?:'s)?\s+jobs?\b/i,
    /\bshow\s+all\b/i, /\blist\s+all\b/i, /\bevery\s+job\b/i, /\ball\s+projects?\b/i,
    /\bportfolio\b/i, /\bdashboard\b/i, /\boverall\s+(summary|status|performance)\b/i,
    /\bcompany\s+(summary|overview|status)\b/i, /\btotal\s+(jobs?|projects?|revenue|margin)\b/i,
    /\bhow\s+many\s+jobs?\b/i, /\bactive\s+jobs?\b/i, /\bmy\s+jobs?\b/i,
    /\bjobs?\s+(overview|summary|list)\b/i, /\bjobs?\s+(?:for|from|by|of)\s+\w+/i,
    /\b\w+(?:'s)?\s+jobs?\b/i,
  ];

  analyze(query: string, context?: ConversationContext): QueryIntent {
    const queryLower = query.toLowerCase();
    let intentType: QueryIntent['type'] = 'general';
    let maxScore = 0;

    for (const [type, patterns] of Object.entries(QueryAnalyzerAgent.INTENT_PATTERNS)) {
      const score = patterns.filter(p => p.test(query)).length;
      if (score > maxScore) { maxScore = score; intentType = type as QueryIntent['type']; }
    }

    let domain: QueryIntent['domain'] = 'general';
    let domainScore = 0;
    for (const [d, keywords] of Object.entries(QueryAnalyzerAgent.DOMAIN_KEYWORDS)) {
      const score = keywords.filter(kw => queryLower.includes(kw)).length;
      if (score > domainScore) { domainScore = score; domain = d as QueryIntent['domain']; }
    }

    const entities = this.extractEntities(query, context);
    const confidence = Math.min(1.0, (maxScore * 0.3 + domainScore * 0.2 + (Object.keys(entities).length > 0 ? 0.3 : 0)));

    let suggestedRefinement: string | undefined;
    if (intentType === 'search' && !entities.jobIds?.length && !entities.jobNames?.length && !entities.clientNames?.length) {
      const searchTerms = this.extractSearchTerms(query);
      if (searchTerms.length === 1 && searchTerms[0].length < 20) {
        suggestedRefinement = `I'll search for "${searchTerms[0]}" in job names, client names, and addresses. Would you like me to search in a specific field?`;
      }
    }

    return { type: intentType, confidence, domain, entities, suggestedRefinement };
  }

  private extractEntities(query: string, context?: ConversationContext): ExtractedEntities {
    const entities: ExtractedEntities = {};

    // Job IDs
    const jobIdMatches = query.match(/(?:job|project)\s*#?\s*(\d+)/gi) || [];
    const hashMatches = query.match(/#(\d+)/g) || [];
    const jobIds = new Set<number>();
    [...jobIdMatches, ...hashMatches].forEach(m => { const num = parseInt(m.replace(/\D/g, '')); if (num > 0) jobIds.add(num); });
    if (jobIds.size > 0) entities.jobIds = Array.from(jobIds);

    // Quoted strings
    const quotedMatches = query.match(/["']([^"']+)["']/g) || [];
    if (quotedMatches.length > 0) entities.searchTerms = quotedMatches.map(m => m.replace(/["']/g, ''));

    // Date references
    if (/this\s+month/i.test(query)) {
      const now = new Date();
      entities.dateRange = { start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
    } else if (/last\s+month/i.test(query)) {
      const now = new Date();
      entities.dateRange = { start: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0], end: new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0] };
    }

    // Metrics
    const metricKeywords = ['margin', 'profit', 'revenue', 'cost', 'hours', 'budget', 'wip', 'outstanding'];
    const foundMetrics = metricKeywords.filter(m => query.toLowerCase().includes(m));
    if (foundMetrics.length > 0) entities.metrics = foundMetrics;

    // Client names
    const clientNames = this.extractClientNames(query);
    if (clientNames.length > 0) {
      entities.clientNames = clientNames;
      if (!entities.searchTerms) entities.searchTerms = [];
      entities.searchTerms.push(...clientNames);
    }

    // Inherit from context
    if (context?.accumulatedEntities && !entities.jobIds && context.accumulatedEntities.jobIds) {
      if (/\b(it|that|this|the job|the project)\b/i.test(query)) {
        entities.jobIds = context.accumulatedEntities.jobIds;
      }
    }

    return entities;
  }

  extractSearchTerms(query: string): string[] {
    const stopWords = new Set(['the', 'a', 'an', 'is', 'are', 'was', 'were', 'about', 'for', 'with', 'tell', 'me', 'show', 'find', 'get', 'what', 'which', 'where', 'how', 'project', 'projects', 'job', 'jobs']);
    return query.toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/).filter(w => w.length > 2 && !stopWords.has(w));
  }

  private extractClientNames(query: string): string[] {
    const clientNames: string[] = [];
    const stopWords = new Set([
      'the', 'a', 'an', 'all', 'my', 'our', 'their', 'active', 'inactive', 'completed',
      'pending', 'done', 'wip', 'new', 'old', 'recent', 'current', 'past', 'future',
      'show', 'find', 'get', 'list', 'display', 'search', 'what', 'which', 'where', 'how',
      'job', 'jobs', 'project', 'projects', 'quote', 'quotes', 'invoice', 'invoices',
      'tell', 'me', 'about', 'for', 'from', 'by', 'of', 'with', 'give', 'every',
    ]);
    const tryAdd = (m: RegExpMatchArray | null) => { if (m) { const p = m[1].trim(); if (!stopWords.has(p.toLowerCase())) clientNames.push(p); } };

    tryAdd(query.match(/\ball\s+([A-Z][A-Za-z\s]*?)(?:'s)?\s+jobs?\b/i));
    tryAdd(query.match(/\b([A-Z][A-Za-z\s]*?)(?:'s)\s+jobs?\b/i));
    tryAdd(query.match(/\bjobs?\s+(?:for|from|by|of)\s+([A-Z][A-Za-z\s]+?)(?:\s*$|\s+(?:and|or|with|in|that|which))/i));
    tryAdd(query.match(/\b(?:show|find|list|get|display)\s+([A-Z][A-Za-z\s]*?)\s+jobs?\b/i));
    tryAdd(query.match(/\bclient[:\s]+([A-Z][A-Za-z\s]+?)(?:\s*$|\s+(?:job|project|quote|and|or))/i));

    return [...new Set(clientNames)];
  }

  isPortfolioQuery(query: string): boolean {
    return QueryAnalyzerAgent.PORTFOLIO_QUERY_PATTERNS.some(p => p.test(query));
  }
}
